// app/components/finance/detail-page/DailyRatesList.tsx
'use client';

import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import React, { useMemo } from 'react';
import { FaCaretDown, FaCaretUp } from 'react-icons/fa';
import { Skeleton } from '../../../../components/ui/skeleton';
import { getDeclinedMonthName } from '../../lib/utils';

interface DailyCurrencyData {
  date: string;
  fullDate: Date;
  value: number;
  change?: number;
}

interface DailyRatesListProps {
  listData: DailyCurrencyData[];
  loading: boolean;
  currencyName: string | undefined;
  monthName: string;
}

// ✅ Мемоизированный компонент для отображения изменения курса
const RenderRateChange = React.memo(({ change }: { change?: number }) => {
  if (change !== undefined && change !== null) {
    if (change === 0) {
      return <span className="text-gray-400">0.00</span>;
    }

    const isPositive = change > 0;
    const colorClass = isPositive ? 'text-green-500' : 'text-red-500';

    return (
      <span className={`inline-flex items-center gap-1 ${colorClass}`}>
        {isPositive ? <FaCaretUp aria-label="повышение" /> : <FaCaretDown aria-label="снижение" />}
        {Math.abs(change).toFixed(2)}
      </span>
    );
  }

  return <span className="text-gray-400">N/A</span>;
});
RenderRateChange.displayName = 'RenderRateChange';

// ✅ Мемоизированный компонент для строки таблицы
const DailyRateRow = React.memo(({ dataPoint }: { dataPoint: DailyCurrencyData }) => (
  <tr className="hover:bg-gray-700 dark:hover:bg-slate-700 transition-colors duration-150">
    <td className="px-3 py-2">
      <time dateTime={dataPoint.date}>
        {format(dataPoint.fullDate, 'dd.MM.yyyy', { locale: ru })}
      </time>
    </td>
    <td className="px-3 py-2 font-bold text-center">{dataPoint.value.toFixed(2)} ₽</td>
    <td className="px-3 py-2 font-bold text-right">
      <RenderRateChange change={dataPoint.change} />
    </td>
  </tr>
));
DailyRateRow.displayName = 'DailyRateRow';

export function DailyRatesList({ listData, loading, currencyName }: DailyRatesListProps) {
  const declinedMonthName = useMemo(() => {
    const monthIndex =
      listData.length > 0 ? listData[0].fullDate.getMonth() : new Date().getMonth();
    return getDeclinedMonthName(monthIndex, 'prepositional');
  }, [listData]);

  return (
    <section
      aria-labelledby="daily-rates-heading"
      className="w-full 2xl:w-1/3 p-4 sm:p-6 bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-lg shadow-lg overflow-hidden max-h-[420px] dark:bg-gradient-to-br dark:from-slate-800/50 dark:via-slate-700/60 dark:to-slate-800/50 dark:border dark:border-slate-700"
      role="region"
    >
      <header className="mb-4">
        <h2
          id="daily-rates-heading"
          className="text-lg sm:text-xl font-semibold text-main"
          aria-live="polite"
        >
          {loading ? (
            <Skeleton className="w-60 h-6" />
          ) : (
            `Курс ${currencyName || ''} в ${declinedMonthName} по дням`
          )}
        </h2>
      </header>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex justify-between items-center py-2 px-3 rounded-md">
              <Skeleton className="w-20 h-5" />
              <Skeleton className="w-28 h-5" />
            </div>
          ))}
        </div>
      ) : (
        <div
          className="overflow-auto max-h-[340px] custom-scrollbar"
          tabIndex={0}
          aria-label="Таблица курсов по дням"
        >
          <table className="min-w-full text-sm text-left text-gray-300">
            <thead className="sticky top-0 z-10 bg-sky-300 dark:bg-slate-800 text-gray-800 dark:text-slate-400 font-bold">
              <tr>
                <th scope="col" className="px-3 py-2">
                  Дата
                </th>
                <th scope="col" className="px-3 py-2 text-center">
                  Курс
                </th>
                <th scope="col" className="px-3 py-2 text-right">
                  Изменение
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-500/40 dark:divide-slate-700">
              {listData.map((dataPoint) => (
                <DailyRateRow key={dataPoint.date} dataPoint={dataPoint} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
